import React, { useEffect, useState } from 'react';
import useApi from '../api';
import { toast } from 'react-toastify';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Loader2, Copy, RefreshCw, Eye, EyeOff } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatDate } from '../utils/dateFormatter';

interface IdentityVerificationStatus {
    configured: boolean;
    enforced: boolean;
    secret: string | null;
    rotated_at: string | null;
}

const nodeSnippet = `import { createHmac } from 'crypto';

// On your server — the secret must never ship to the browser.
// Sign "external_id:email" with the email trimmed and lowercased;
// both values must match what you pass to identify.
// Identifies with an account use the canonical v2 payload in the SDK docs.
const userHash = createHmac('sha256', process.env.AIMDOC_IDENTITY_SECRET)
  .update(\`\${user.id}:\${user.email.trim().toLowerCase()}\`)
  .digest('hex');`;

const clientSnippet = `import { aimdoc } from '@aimdoc/sdk';

aimdoc.identify({
  external_id: user.id,
  email: user.email,
  user_hash: userHashFromServer,
});`;

const IdentityVerification: React.FC = () => {
    const { get, post, put } = useApi();

    const [status, setStatus] = useState<IdentityVerificationStatus | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [working, setWorking] = useState<boolean>(false);
    const [showSecret, setShowSecret] = useState<boolean>(false);

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        setLoading(true);
        try {
            const response = await get('/identity-verification');
            setStatus(response.data || response);
        } catch (error) {
            console.error('Failed to load identity verification settings:', error);
            toast.error('Failed to load identity verification settings');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateOrRotate = async () => {
        if (status?.configured) {
            const confirmed = confirm(
                'Rotate the signing secret? Identify calls signed with the current secret will stop verifying until your server uses the new one.'
            );
            if (!confirmed) return;
        }
        setWorking(true);
        try {
            const response = await post('/identity-verification/secret', {});
            setStatus(response.data || response);
            setShowSecret(true);
            toast.success(status?.configured ? 'Secret rotated' : 'Secret generated');
        } catch (error) {
            console.error('Failed to generate secret:', error);
            toast.error('Failed to generate secret');
        } finally {
            setWorking(false);
        }
    };

    const handleEnforcedChange = async (enforced: boolean) => {
        if (enforced) {
            const confirmed = confirm(
                'Enforce identity verification? Identify calls without a valid user_hash will be rejected. Make sure all your apps are sending signed identifies first.'
            );
            if (!confirmed) return;
        }
        setWorking(true);
        try {
            const response = await put('/identity-verification', { enforced });
            setStatus(response.data || response);
            toast.success(enforced ? 'Enforcement enabled' : 'Enforcement disabled');
        } catch (error) {
            console.error('Failed to update enforcement:', error);
            toast.error('Failed to update enforcement');
        } finally {
            setWorking(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[350px]">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-3xl">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5" />
                        Identity Verification
                    </CardTitle>
                    <CardDescription>
                        Prove that identify calls come from your app. Your server signs each
                        user's external_id and email with this shared secret (HMAC-SHA256);
                        Aimdoc verifies the signature and marks the contact's identity as
                        verified. Account identify payloads use the same secret with a
                        versioned signature covering every account field.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {!status?.configured ? (
                        <div className="space-y-4">
                            <Alert>
                                <AlertDescription>
                                    No signing secret yet. Generate one, add it to your server
                                    environment, and start passing <code>user_hash</code> in your
                                    identify calls. Unsigned identifies keep working until you
                                    enable enforcement.
                                </AlertDescription>
                            </Alert>
                            <Button onClick={handleGenerateOrRotate} disabled={working}>
                                {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                                Generate signing secret
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <Label>Signing secret</Label>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono break-all">
                                        {showSecret ? status.secret : '•'.repeat(24)}
                                    </code>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setShowSecret(!showSecret)}
                                        title={showSecret ? 'Hide secret' : 'Show secret'}
                                    >
                                        {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => status.secret && copyToClipboard(status.secret)}
                                        title="Copy secret"
                                    >
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={handleGenerateOrRotate}
                                        disabled={working}
                                        title="Rotate secret"
                                    >
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Rotate
                                    </Button>
                                </div>
                                {status.rotated_at && (
                                    <p className="text-xs text-muted-foreground">
                                        Generated {formatDate(status.rotated_at)}
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center justify-between rounded-lg border p-4">
                                <div className="space-y-0.5">
                                    <Label>Enforce identity verification</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Reject identify calls that don't include a valid
                                        user_hash. Enable only after all your apps send signed
                                        identifies.
                                    </p>
                                </div>
                                <Switch
                                    checked={status.enforced}
                                    onCheckedChange={handleEnforcedChange}
                                    disabled={working}
                                />
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Setup</CardTitle>
                    <CardDescription>
                        1. Compute the hash on your server. 2. Pass it to identify in the browser.
                        See the SDK account-identify docs for the canonical v2 payload.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label>Server (Node.js)</Label>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(nodeSnippet)}>
                                <Copy className="h-3 w-3 mr-1" /> Copy
                            </Button>
                        </div>
                        <pre className="rounded bg-muted p-3 text-xs overflow-x-auto"><code>{nodeSnippet}</code></pre>
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label>Client</Label>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(clientSnippet)}>
                                <Copy className="h-3 w-3 mr-1" /> Copy
                            </Button>
                        </div>
                        <pre className="rounded bg-muted p-3 text-xs overflow-x-auto"><code>{clientSnippet}</code></pre>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default IdentityVerification;
